/**
 * --------------------------------------------------------------------------
 * NJ : Slider.js
 * --------------------------------------------------------------------------
 */

import { Data, EventHandler, Manipulator, Util } from 'nj-utils';
import { DEFAULT_OPTIONS as TOOLTIP_OPTIONS, SELECTOR as TOOLTIP_SELECTOR } from 'nj-components/Tooltip';
import AbstractComponent from 'nj-components/AbstractComponent';
import { coreConfig } from 'nj-components/core.config';

/**
 * ------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------
 */

const NAME       = 'nj-slider';
const DATA_KEY   = `${coreConfig.KEY_PREFIX}.slider`;
const CLASS_NAME = `${coreConfig.KEY_PREFIX}-slider`;

const SELECTOR   = {
  default : `.${CLASS_NAME}`,
  input   : 'input',
  label   : 'label'
};

const THUMB_WIDTH = 16; // thumb width in pixel

const DEFAULT_TYPE = {
  tooltip : 'boolean'
};

const DEFAULT_OPTIONS = {
  tooltip : false
};

const PERCENT_CONV = 100;

const PSEUDO_ELEMS = ['webkit-slider-runnable', 'moz-range', 'ms'];

/**
 * ------------------------------------------------------------------------
 * Class Definition
 * ------------------------------------------------------------------------
 */

class Slider extends AbstractComponent {
  constructor(element, config) {
    super(Slider, element);

    this.config = this.getConfig(config);
    // eslint-disable-next-line no-magic-numbers
    this.dataId = Number(String(Math.random()).slice(2)) + Date.now();
    this.element = element;
    this.input = this.element.querySelector(SELECTOR.input);
    this.isIe11 = Boolean(window.MSInputMethodContext) && Boolean(document.documentMode);
    this.dynamicStyles = document.createElement('style');

    this.element.setAttribute('data-id', this.dataId);
    document.body.appendChild(this.dynamicStyles);

    this.refreshProgressValue();
    this.setListeners();

    if (this.config.tooltip) {
      this.addTooltip();
      this.setTooltipListeners();
    }

    Data.setData(element, DATA_KEY, this);
  }

  addTooltip() {
    this.tooltip = Manipulator.createHtmlNode(TOOLTIP_OPTIONS.template);
    this.tooltip.classList.add('nj-tooltip--top');
    this.tooltip.classList.add('show');
    this.element.insertBefore(this.tooltip, this.element.querySelector(SELECTOR.label));
    this.refreshTooltipValue();
  }

  setListeners() {
    EventHandler.on(this.element, 'input change keyup', () => {
      this.refreshProgressValue();
    });
  }

  setTooltipListeners() {
    EventHandler.on(this.element, 'input change keyup', () => {
      this.refreshTooltipValue();
    });

    const delay = 100;
    let throttled = false;

    EventHandler.on(window, 'resize', () => {
      if (!throttled) {
        this.refreshTooltipValue();
        throttled = true;
        setTimeout(() => {
          throttled = false;
        }, delay);
      }
    });
  }

  refreshProgressValue() {
    const max = this.input.max || PERCENT_CONV;
    const min = this.input.min || 0;
    const value = this.input.value;

    const perc = Math.floor(PERCENT_CONV * (value - min) / (max - min));

    let styles = '';
    PSEUDO_ELEMS.forEach((PSEUDO_ELEM) => {
      styles += `${SELECTOR.default}[data-id='${this.dataId}'] input[type=range]::-${PSEUDO_ELEM}-track{background-size: ${perc}% 100%} `;
    });
    this.dynamicStyles.textContent = styles;
  }

  refreshTooltipValue() {
    this.tooltip.querySelector(TOOLTIP_SELECTOR.inner).innerHTML = this.input.value;
    this.replaceTooltip();
  }

  replaceTooltip() {
    const min = this.input.min === '' ? 0 : parseInt(this.input.min, 10);
    // eslint-disable-next-line no-magic-numbers
    const max = this.input.max === '' ? 100 : parseInt(this.input.max, 10);

    const val = parseInt(this.input.value, 10);
    const pos = (val - min) / (max - min);

    if (this.isIe11) {
      // eslint-disable-next-line no-magic-numbers
      this.tooltip.style.left = `${pos * (this.input.offsetWidth - THUMB_WIDTH - 6) - this.tooltip.offsetWidth / 2 + THUMB_WIDTH / 2 + 3}px`;
    } else {
      // eslint-disable-next-line no-magic-numbers
      this.tooltip.style.left = `${pos * (this.input.offsetWidth - THUMB_WIDTH) - this.tooltip.offsetWidth / 2 + THUMB_WIDTH / 2}px`;
    }
  }

  getConfig(config) {
    config = {
      ...DEFAULT_OPTIONS,
      ...Manipulator.getDataAttributes(this.element),
      ...typeof config === 'object' && config ? config : {}
    };

    Util.typeCheckConfig(
      NAME,
      config,
      DEFAULT_TYPE
    );

    return config;
  }

  dispose() {
    Data.removeData(this.element, DATA_KEY);
    this.element = null;
  }

  static getInstance(element) {
    return Data.getData(element, DATA_KEY);
  }

  static init(options) {
    return super.init(this, options, SELECTOR.default);
  }

  static selector = SELECTOR.default;
}

export default Slider;
